import { HeatingCircuit, HeatingMode, Pump, ThreewayTap, TimeInterval } from 'z3k-config';
import { getPumpId, getTapId } from 'console/climatic/utils/getters';
import { dhw_boiler_pump_id } from 'console/climatic/constants';
import { useContext } from 'react';
import { Z3KSettings, Z3KSettingsEditor } from 'z3k-settings/provider/types';
import { find_interval_schedule } from 'console/climatic/setting-tabs/circuit';
import { useGetZ3kObject } from 'z3k-settings/components/utils/unpack-props';
export function useHeatingCircuitsItemState() {
    const for_circuit = useGetZ3kObject('heating_circuits');
    const { config } = useContext(Z3KSettingsEditor);
    const { device_ui_settings, deviceFirmwareVersion } = useContext(Z3KSettings);
    const circuit = HeatingCircuit.get.byId(config, for_circuit.id) ?? for_circuit;
    const timetable = find_interval_schedule(config, circuit.id);
    let time_intervals = [];
    if (timetable != null)
        time_intervals = TimeInterval.get.idIn(config, timetable.time_intervals);
    const circ_pump_id = getPumpId(config?.object_arrays, config?.pumps, circuit.id);
    const circulation_pump = circ_pump_id != null ? Pump.get.byId(config, circ_pump_id) : undefined;
    const boiler_pump = Pump.get.byId(config, dhw_boiler_pump_id);
    let dhw_parallel_heating = false;
    if (circuit.type === HeatingCircuit.Type.DHW) {
        dhw_parallel_heating = !HeatingCircuit.get
            .where(config, (hc) => hc.type === HeatingCircuit.Type.HEATING)
            .every(HeatingCircuit.isBlockedWhileDHW);
    }
    const threeway_tap_id = getTapId(config?.object_arrays, config?.threeway_taps, circuit.id);
    const threeway_tap = threeway_tap_id != null
        ? ThreewayTap.get.byId(config, threeway_tap_id)
        : undefined;
    return {
        for_circuit,
        firmware_version: deviceFirmwareVersion,
        circuit: circuit,
        timetable: timetable,
        time_intervals: time_intervals,
        object_arrays: config?.object_arrays ?? [],
        // pumps: device.z3k_config?.pumps ?? [],
        analog_temperature_sensors: config?.analog_temperature_sensors ?? [],
        // Смеситель
        threeway_tap: threeway_tap,
        state_tap: threeway_tap
            ? {
                step_time: threeway_tap?.step_time,
                coefficient: threeway_tap?.coefficient,
                closing_time: threeway_tap?.closing_time,
                step_period: threeway_tap?.step_period,
            }
            : undefined,
        target_temperatures: HeatingMode.get
            .all(config)
            .filter((mode) => mode.heating_zones.some((zone) => zone.heating_circuit === circuit.id))
            .filter((mode) => HeatingMode.getTargetTemperature(mode, circuit.id) != null)
            .map((mode) => [
            mode,
            HeatingMode.getTargetTemperature(mode, circuit.id),
        ])
            .filter(([, temp]) => temp != null),
        start_times: Object.fromEntries(time_intervals.map(({ id, sh, sm }) => [id, { hour: sh, min: sm }])),
        end_times: Object.fromEntries(time_intervals.map(({ id, eh, em }) => [id, { hour: eh, min: em }])),
        heating_modes: HeatingMode.get.all(config),
        winter_summer_switch: circuit.winter_summer_switch,
        summer_threshold: circuit.summer_threshold,
        // enable_pza: circuit.pza !== 0,
        pza: circuit.pza != null && [0, 255].includes(circuit.pza) ? 1.2 : circuit.pza,
        hysteresis: circuit.hysteresis,
        heat_request: circuit.heat_request,
        setting_register: circuit.setting_register,
        turn_off_delay: Math.round(circuit.turn_off_delay / 60),
        circulation_pump_delay: circulation_pump?.delay ?? null,
        circulation_pump_id: circ_pump_id,
        circulation_pump_mode: circulation_pump != null
            ? Pump.getMode(circulation_pump)
            : Pump.Mode.BY_HEAT_REQUEST,
        boiler_pump_delay: boiler_pump?.delay ?? null,
        water_min_temperature: typeof circuit.water_min_temperature === 'string'
            ? +circuit.water_min_temperature
            : circuit.water_min_temperature,
        water_max_temperature: typeof circuit.water_max_temperature === 'string'
            ? +circuit.water_max_temperature
            : circuit.water_max_temperature,
        control_mode: circuit.type === HeatingCircuit.Type.HEATING
            ? HeatingCircuit.getControlModeClimatic(circuit)
            : null,
        dhw_use_circ_pump: circuit.type === HeatingCircuit.Type.DHW &&
            circ_pump_id != null &&
            circuit.control_devices.includes(circ_pump_id),
        dhw_parallel_heating,
        antileg_enabled: HeatingCircuit.isAntilegEnabled(circuit),
        antileg_time: {
            hour: circuit.antileg_mode_options.h,
            min: circuit.antileg_mode_options.m,
        },
        antileg_days: circuit.antileg_mode_options.days,
        icon: device_ui_settings?.icons?.heating_circuits?.[circuit.id],
        no_stop_heating_when_pid_is_in_low_threshold: HeatingCircuit.isNoStopHeatingWhenPidIsInLowThreshold(circuit),
    };
}
